import { useContext, useEffect, useRef, useState } from 'react';
import { delay } from 'msw';
import frontEndMessageHub, { IFrontEndMessageHub } from '@utils-clean/front-end-message-hub';
import { UserContext } from '@contexts/user-context';

export interface MessageHubHookProps {
    messageHubRelativeEndpoint: string;
    entityType: string;
    parentId: string | undefined;
    onCommentsUpdated: (commentIds: string[]) => Promise<boolean>;
    onCommentDeleted: (isSelfMessage: boolean, commentId: string) => void;
}

export interface IMessageHubHook {
    connectedAtLeastOnce: boolean;
    disconnect: () => void;
    startProcessingUpdates: () => void;
}

const useCommentsMessageHub = ({
    messageHubRelativeEndpoint,
    parentId,
    onCommentsUpdated,
    onCommentDeleted,
}: MessageHubHookProps): IMessageHubHook => {
    const { user } = useContext(UserContext);

    // Fixed for the lifetime of the component. Changing this requires remounting the comments sidebar
    const messageHubRef = useRef<IFrontEndMessageHub>(undefined);
    if (!messageHubRef.current) {
        messageHubRef.current = frontEndMessageHub(messageHubRelativeEndpoint);
    }
    const { hubConnection, connectToHub, disconnectFromHub } = messageHubRef.current;

    const connectRetryAbortController = useRef<AbortController>(new AbortController());

    const [connectedAtLeastOnce, setConnectedAtLeastOnce] = useState(false);
    const updateQueue = useRef<Set<string>>(new Set());
    const updatesPaused = useRef<boolean>(true);
    const processingUpdates = useRef<boolean>(false);

    const processQueuedUpdatesRecursive = async () => {
        if (updatesPaused.current || !(updateQueue.current.size > 0)) {
            return;
        }

        const commentIds = [...updateQueue.current];
        updateQueue.current.clear();

        const success = await onCommentsUpdated(commentIds);

        if (!success) {
            updateQueue.current = new Set([...commentIds, ...updateQueue.current]);
            await delay(5000);
        }

        await processQueuedUpdatesRecursive();
    };

    const processQueuedUpdates = async () => {
        if (processingUpdates.current) {
            return;
        }

        processingUpdates.current = true;
        await processQueuedUpdatesRecursive();
        processingUpdates.current = false;
    };

    const startProcessingUpdates = () => {
        updatesPaused.current = false;

        // eslint-disable-next-line no-void
        void processQueuedUpdates();
    };

    const connectEndlessRetry = async (abortController: AbortController) => {
        if (abortController.signal.aborted) {
            return;
        }

        try {
            await connectToHub(parentId!);
            if (abortController.signal.aborted) {
                disconnectFromHub();
            }
            setConnectedAtLeastOnce(true);
        } catch {
            await delay(5000);
            await connectEndlessRetry(abortController);
        }
    };

    const disconnect = () => {
        hubConnection.off('CommentUpdated');
        hubConnection.off('CommentDeleted');
        updatesPaused.current = true;
        connectRetryAbortController.current.abort();
        disconnectFromHub();
    };

    useEffect(() => {
        if (parentId) {
            hubConnection.on('CommentUpdated', (data) => {
                updateQueue.current.add(data.id);
                // eslint-disable-next-line no-void
                void processQueuedUpdates();
            });

            hubConnection.on('CommentDeleted', (data) => {
                onCommentDeleted(user?.id === data.publisherUserId, data.id);
            });

            // eslint-disable-next-line no-void
            void connectEndlessRetry(connectRetryAbortController.current);

            hubConnection.onreconnecting(() => connectRetryAbortController.current.abort());

            hubConnection.onreconnected(() => {
                connectRetryAbortController.current.abort();
                connectRetryAbortController.current = new AbortController();
                // eslint-disable-next-line no-void
                void connectEndlessRetry(connectRetryAbortController.current);
            });
        }

        return () => {
            disconnect();
        };
    }, [parentId]);

    return { connectedAtLeastOnce, disconnect, startProcessingUpdates };
};

export default useCommentsMessageHub;
